import React, { useState, useEffect } from "react";
import {
  Breadcrumbs,
  Typography,
  Stack,
  Divider,
  Button,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Box } from "@mui/system";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { API } from "aws-amplify";

import { HeadingPrimary } from "../../components/Styles/Heading";
import { Input } from "../../components/Styles/Input";
import { authData } from "../../redux/auth/authSlice";
import config, { Theme } from "../../config/aws-exports";
import Swal from "sweetalert2";
import "./profile.css";

const Profile = () => {
  const userDetails = useSelector(authData);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    stdcode: "",
    cno: "",
    designation: "",
    department: "",
    organization: "",
    busines_unit: "",
    country: "",
    state: "",
    city: "",
    pin: "",
    gstnumber: "",
    pan: "",
    ur_id: "",
    gen: "",
    dev: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfile = async () => {
    try {
      setIsLoading(true);

      const res = await API.post(
        config.aws_cloud_logic_custom_name,
        "/getUserDetails",
        {
          body: {
            emailid: userDetails?.email,
            schema: config.schema,
          },
        },
      );

      const user = res?.body?.[0] || {};

      setFormData({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.emailid || "",
        stdcode: user.stdcode || "",
        cno: user.cno || "",
        designation: user.designation || "",
        department: user.department || "",
        organization: user.organization || "",
        busines_unit: user.busines_unit || "",
        country: user.country || "",
        state: user.state || "",
        city: user.city || "",
        pin: user.pin || "",
        gstnumber: user.gstnumber || "",
        pan: user.pan || "",
        ur_id: user?.ur_id,
        gen: user.gen,
        dev: user.dev,
      });
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const validateForm = () => {
    let newErrors = {};

    if (!formData.first_name?.trim()) {
      newErrors.first_name = "Please enter first name";
    } else if (!/^[a-zA-Z ]*$/.test(formData.first_name)) {
      newErrors.first_name = "Only alphabets allowed";
    }

    if (!formData.last_name?.trim()) {
      newErrors.last_name = "Please enter last name";
    }

    if (!formData.email?.trim()) {
      newErrors.email = "Please enter email";
    }

    if (!formData.stdcode || formData.stdcode.toString().length < 2) {
      newErrors.stdcode = "Invalid STD code";
    }

    if (!formData.cno || formData.cno.toString().length < 10) {
      newErrors.cno = "Phone must be at least 10 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setIsLoading(true);

      const payload = {
        body: {
          ...formData,
          type: "edit",
          officeAddress: formData.busines_unit,
          gst: formData.gstnumber,
          atype: 1,
          schema: config.schema,
          oid: config.aws_org_id,
        },
      };

      await API.post(config.aws_cloud_logic_custom_name, "/addUser", payload);

      setIsEditing(false);

      Swal.fire("Success", "Profile updated successfully", "success");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Somthing went wrong", "error");
    } finally {
      fetchProfile();
      setIsLoading(false);
    }
  };

  const renderField = (label, field, multiline = false) => (
   
<Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1}
      sx={{ width: "100%" }}
    >
      <Box
        sx={{
          width: { xs: "100%", sm: "150px" },
          fontWeight: 500,
          flexShrink: 0,
        }}
      >
        {label}
      </Box>

    
 <Box sx={{ flex: 1, width: "100%" }}>
        <Input
          readOnly={field === "email" || !isEditing}
          value={formData[field] || ""}
          fullWidth
          multiline={multiline}
          minRows={multiline ? 2 : undefined}
          onChange={(e) => handleChange(field, e.target.value)}
          error={!!errors[field]}
          className={`textbox ${isEditing ? "editing" : ""}`}
        />
      </Box>

      {errors[field] && (
        <Typography color="error" variant="caption">
          {errors[field]}
        </Typography>
      )}
    </Stack>
  );


  return (
    <Box sx={{ width: "100%", maxWidth: 1050 }}>
      <Stack spacing={4} m={{ xs: 4, sm: 6, md: 8 }}>
        {/* Breadcrumb */}
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
          <Link to="/dashboard">Dashboard</Link>
          <Typography color={Theme.color.text}>My profile</Typography>
        </Breadcrumbs>

        <HeadingPrimary>My Profile</HeadingPrimary>
        <Divider />

        <Stack direction={{ xs: "column", md: "row" }}  spacing={{ xs: 4, md: 8 }}>
          <Stack spacing={3} flex={1}>
            {renderField("First Name :", "first_name")}
            {renderField("Last Name :", "last_name")}
            {renderField("Email :", "email")}
            {renderField("Phone :", "cno")}
            {renderField("Designation :", "designation")}
            {renderField("Department :", "department")}
            {renderField("Organization :", "organization")}
          </Stack>

          <Stack spacing={3} flex={1}>
            {renderField("Office Address :", "busines_unit", true)}
            {renderField("City :", "city")}
            {renderField("State :", "state")}
            {renderField("Country :", "country")}
            {renderField("Pin Code :", "pin")}
            {renderField("GST :", "gstnumber")}
            {renderField("PAN :", "pan")}
          </Stack>
        </Stack>

        {/* Actions */}
        <Stack direction="row" justifyContent="center" spacing={3}>
          {!isEditing ? (
            <Button
              variant="contained"
              onClick={() => setIsEditing(true)}
              sx={{ bgcolor: Theme.color.secondary }}
            >
              Edit
            </Button>
          ) : (
            <>
              <Button
                onClick={() => setIsEditing(false)}
                sx={{
                  color: Theme.color.secondary,
                  border: `1px solid ${Theme.color.secondary}`,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                sx={{ bgcolor: Theme.color.secondary }}
              >
                Submit
              </Button>
            </>
          )}
        </Stack>

        <Divider />
      </Stack>

      {/* Loader */}
      <Backdrop
        open={isLoading}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress />
      </Backdrop>
    </Box>
  );
};

export default Profile;
